import { Control, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Select, TextField } from '@fly/core';

import { GenerateTokenFormData } from './GenerateTokenForm.schema';
import {
  useExpirationTimeOptions,
  usePermissionsOptions,
} from './GenerateTokenForm.utils';

// Allow T to extend the base data so we can pass a control with MORE fields
interface GenerateTokenFormProps<T extends GenerateTokenFormData> {
  control: Control<T>;
  className?: string;
  isPermissionsReadOnly?: boolean;
}

export function GenerateTokenForm<T extends GenerateTokenFormData>({
  control,
  className,
  isPermissionsReadOnly,
}: GenerateTokenFormProps<T>) {
  const { t } = useTranslation();
  const expirationTimeOptions = useExpirationTimeOptions();
  const permissionsOptions = usePermissionsOptions();

  return (
    <div className={`flex flex-col gap-fly-5 ${className || ''}`}>
      <Controller
        control={control}
        name={'tokenName' as Path<T>}
        render={({ field }) => (
          <TextField
            required
            data-testid='token-name-input'
            placeholder={t('app.dashboard.token_management.fields.token_name', {
              defaultValue: 'Token Name',
            })}
            label={t('app.dashboard.token_management.fields.token_name', {
              defaultValue: 'Token Name',
            })}
            {...field}
          />
        )}
      />
      <Controller
        control={control}
        name={'expirationTime' as Path<T>}
        render={({ field }) => (
          <Select
            data-testid='token-expiration-select'
            size='2'
            required
            label={t('app.dashboard.token_management.fields.expiration_date', {
              defaultValue: 'Expiration Date',
            })}
            options={expirationTimeOptions}
            {...field}
          />
        )}
      />
      <Controller
        control={control}
        name={'permissions' as Path<T>}
        render={({ field }) => (
          <Select
            data-testid='token-permission-select'
            size='2'
            required={!isPermissionsReadOnly}
            label={t('app.dashboard.token_management.fields.permission', {
              defaultValue: 'Permission',
            })}
            options={permissionsOptions}
            disabled={isPermissionsReadOnly}
            {...field}
          />
        )}
      />
    </div>
  );
}
